import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Hourglass } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BehaviorPersonaEnum,
  LifestylePersonaEnum,
  RoommatePersonaEnum,
} from "@/__generated__/types";
import { RiseStar } from "@/assets/svg/RiseStar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  BEHAVIOR_PERSONAS,
  LIFESTYLE_PERSONAS,
  ROOMMATE_PERSONAS,
  PERSONA_COLORS,
  PersonaType,
  PersonaData,
} from "./personaData";
import { Student } from "@/types/Student";

interface PersonasSectionProps {
  student: Student;
  loading?: boolean;
}

interface PersonaCardProps {
  type: PersonaType;
  personaData: PersonaData | null;
  studentName: string;
  onReadMore: () => void;
}

const PersonaCard: React.FC<PersonaCardProps> = ({
  type,
  personaData,
  studentName,
  onReadMore,
}) => {
  const { t } = useTranslation();
  const colors = PERSONA_COLORS[type];

  if (!personaData) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden rounded-[14px] border border-dashed border-[rgba(0,0,0,0.2)] bg-[rgba(213,213,213,0.1)] p-[3px]">
        <div className="flex h-[150px] flex-col overflow-hidden rounded-xl px-4 py-6">
          <div className="flex flex-1 flex-col">
            <div className="flex flex-1 flex-col gap-1">
              <div className="size-10">
                <Hourglass className="size-10 text-slate-400" strokeWidth={1} />
              </div>
              <p className="text-[17px] leading-[1.2] font-bold text-slate-700 opacity-60">
                {t("studentCard.personas.emptyState.title")}
              </p>
            </div>
            <p className="text-sm text-slate-900">
              {t("studentCard.personas.emptyState.message", {
                name: studentName,
              })}
            </p>
          </div>
        </div>
        <div className="flex h-10 items-center justify-center border-t border-dashed border-[rgba(0,0,0,0.2)]">
          <p className="text-xs font-extrabold tracking-[1.2px] text-slate-700 uppercase opacity-20">
            {t(`studentCard.personas.${type}`)}
          </p>
        </div>
      </div>
    );
  }

  const IconComponent = personaData.icon;

  return (
    <div
      className={cn(
        "flex flex-1 flex-col overflow-hidden rounded-[14px] bg-gradient-to-b p-[3px]",
        colors.gradient,
      )}
    >
      <div
        className={cn(
          "flex h-[150px] flex-col overflow-hidden rounded-xl border bg-background px-4 py-6 shadow-sm",
          colors.border,
        )}
      >
        <div className="flex flex-1 flex-col justify-between">
          <div className="flex flex-col gap-1">
            <div className="size-10 rounded-lg p-1">
              <IconComponent className={cn("size-8", colors.text)} />
            </div>
            <p
              className={cn(
                "text-[17px] leading-[1.2] font-bold opacity-80",
                colors.text,
              )}
            >
              {personaData.title}
            </p>
          </div>
          <button onClick={onReadMore} className="w-fit text-sm text-blue-600">
            {t("studentCard.personas.readMore")}
          </button>
        </div>
      </div>
      <div className="flex h-10 items-center justify-center overflow-hidden">
        <p
          className={cn(
            "text-xs font-extrabold tracking-[1.2px] uppercase",
            colors.label,
          )}
        >
          {t(`studentCard.personas.${type}`)}
        </p>
      </div>
    </div>
  );
};

const PersonasSkeleton: React.FC = () => {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-[18px] w-[18px]" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="mt-1.5 mb-4 h-5 w-96" />
      <div className="flex gap-4 pt-2 pb-6">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[193px] flex-1 rounded-[14px]" />
        ))}
      </div>
    </div>
  );
};

export const PersonasSection: React.FC<PersonasSectionProps> = ({
  student,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectedPersona, setSelectedPersona] = useState<{
    type: PersonaType;
    data: PersonaData;
  } | null>(null);

  if (loading) {
    return <PersonasSkeleton />;
  }

  const studentStatus = student?.studentOrganizationStatus;
  const riseData = studentStatus?.rise;
  const studentName = studentStatus?.firstName || "";

  const lifestylePersona = riseData?.lifestylePersona as
    | LifestylePersonaEnum
    | null
    | undefined;
  const behaviorPersona = riseData?.behaviorPersona as
    | BehaviorPersonaEnum
    | null
    | undefined;
  const roommatePersona = riseData?.roommatePersona as
    | RoommatePersonaEnum
    | null
    | undefined;

  const lifestyleData = lifestylePersona
    ? LIFESTYLE_PERSONAS[lifestylePersona]
    : null;
  const behaviorData = behaviorPersona
    ? BEHAVIOR_PERSONAS[behaviorPersona]
    : null;
  const roommateData = roommatePersona
    ? ROOMMATE_PERSONAS[roommatePersona]
    : null;

  const handleReadMore = (type: PersonaType, data: PersonaData) => {
    setSelectedPersona({ type, data });
  };

  const closeDialog = () => {
    setSelectedPersona(null);
  };

  const colors = selectedPersona ? PERSONA_COLORS[selectedPersona.type] : null;

  return (
    <div className="px-0">
      <div className="flex items-start">
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex items-center gap-2">
            <RiseStar />
            <p className="leading-none font-bold text-card-foreground">
              {t("studentCard.personas.title")}
            </p>
          </div>
          <p className="text-sm leading-5 text-muted-foreground">
            {t("studentCard.personas.description", { name: studentName })}
          </p>
        </div>
      </div>

      <div className="flex gap-4 pt-4">
        <PersonaCard
          type="lifestyle"
          personaData={lifestyleData}
          studentName={studentName}
          onReadMore={() =>
            lifestyleData && handleReadMore("lifestyle", lifestyleData)
          }
        />
        <PersonaCard
          type="behavior"
          personaData={behaviorData}
          studentName={studentName}
          onReadMore={() =>
            behaviorData && handleReadMore("behavior", behaviorData)
          }
        />
        <PersonaCard
          type="roommate"
          personaData={roommateData}
          studentName={studentName}
          onReadMore={() =>
            roommateData && handleReadMore("roommate", roommateData)
          }
        />
      </div>

      <Dialog open={!!selectedPersona} onOpenChange={closeDialog}>
        <DialogContent className="flex flex-col gap-2 rounded-lg border bg-background p-6 shadow-lg">
          <button
            onClick={closeDialog}
            className="absolute top-4 right-4 opacity-70 transition-opacity hover:opacity-100"
            aria-label={t("global.close")}
          >
            <X className="size-4" />
          </button>
          {selectedPersona && colors && (
            <>
              <DialogHeader className="flex flex-col items-start gap-1.5">
                <div className="size-10 rounded-lg p-1">
                  <selectedPersona.data.icon
                    className={cn("size-8", colors.text)}
                  />
                </div>
                <DialogTitle
                  className={cn(
                    "text-[17px] leading-[1.2] font-bold opacity-80",
                    colors.text,
                  )}
                >
                  {selectedPersona.data.title}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <DialogDescription className="text-sm leading-5 text-foreground">
                  {selectedPersona.data.description}
                </DialogDescription>
                <div>
                  <p className="mb-2 font-semibold text-foreground">
                    {t("studentCard.personas.communicationTips")}
                  </p>
                  <ul className="flex flex-col gap-1">
                    {selectedPersona.data.communicationTips.map(
                      (tip, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-sm leading-5 text-foreground"
                        >
                          <span className="text-foreground">•</span>
                          <span>{tip}</span>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
